import { React, Link, useNavigate, useState, dash, CssBaseline, TextField, Typography, Box, Grid2, rsk_logo } from '../utils/globalImports';
import { CustomButton, Footer, Toaster } from '../utils/globalImports';
import useApi from '../hooks/useApi';

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [openToaster, setOpenToaster] = useState(false); // State for the toaster
  const [isOtpSent, setIsOtpSent] = useState(false); // State to track OTP sent status
  const { execute, loading } = useApi();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
  
    // Check if there is an internet connection before doing anything else
    if (!navigator.onLine) {
      setEmailError('No internet connection. Please check your connection and try again.');
      return;
    }
  
    // Proceed with the form validation
    localStorage.setItem('email', email);
  
    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address.');
      return;
    }
  
    setEmailError('');
    setSuccessMessage('');
  
    try {
      const response = await execute('post', 'Auth/forgot-password', { email });
  
      if (response.data) {
        setSuccessMessage('Password reset OTP sent successfully!');
        setIsOtpSent(true);
        setOpenToaster(true);
        setTimeout(() => {
          setOpenToaster(false);
          navigate('/verify-otp');
        }, 3000);
      } else if (response.error) {
        setEmailError(response.error === 'User not found.' ? 'Email not found. Please try again.' : 'User with provided email does not exist');
      }
    } catch (error) {
      setEmailError('Something went wrong. Please try again later.');
    }
  };
  
  const validateEmail = (email: string) => {
    // Email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  return (
    <>
      <CssBaseline />
      <Grid2 container className="login-container" spacing={2}>
        {/* Left Section */}
        <Grid2 className="left-section">
          <Box className="header">
            <Typography component="h1" variant="h5" className="title">
              Forgot Password
            </Typography>
            <img src={dash} alt="Dash" className="dash" />
          </Box>
          <Typography className="subheader">
            Enter your email address to reset your password
          </Typography>
          <Box component="form" noValidate onSubmit={handleSubmit}>
            <Typography className="input-label">Email Address</Typography>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              name="email"
              placeholder="Enter your email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="text-field"
            />
            {emailError && (
              <Typography className="error-message" color="error">
                {emailError}
              </Typography>
            )}
            {successMessage && (
              <Typography className="success-message" color="primary">
                {successMessage}
              </Typography>
            )}
            <CustomButton 
              capsule 
              className="login-button" 
              disabled={loading || isOtpSent} // Disable button if OTP is sent or loading
            >
              {loading ? 'Sending...' : isOtpSent ? 'OTP Sent Successfully' : 'Send OTP'}
            </CustomButton>
            <Link to="/login" className="login-link">
              Return to Login
            </Link>
          </Box>
        </Grid2>

        {/* Right Section */}
        <Grid2 className="right-section">
          <img src={rsk_logo} alt="Logo" className="logo" />
          <Typography className="title">
            Start managing your finance faster and better
          </Typography>
        </Grid2>
      </Grid2>
      <Footer />

      {/* Toaster Notification */}
      <Toaster 
        open={openToaster} 
        message="OTP sent successfully!" 
        type="success" 
        onClose={() => setOpenToaster(false)} 
      />
    </>
  );
};

export default ForgotPassword;